// BetaTech (2026-10-27, client request): when the order arrives, from the delivery country.
// "Arrin më 28–29 tetor" + "1–2 ditë pune · Kosovë · Dërgesa 2 €" with the theme's truck icon
// (snippets/beta-arrival.liquid), in the retail cart drawer and on the cart page; built so the checkout can use the
// same element (BetaEta.fill(el, { country: 'AL' }) when the buyer picks another country).
//
// Computed in the browser, because pages are cached: a date printed by Liquid would go stale.
// Rules (theme settings "BetaTech – Faqja e produktit", passed by the snippet as JSON):
// - "now" is read in the shop's time zone (Kosovo, Europe/Belgrade), whatever the device's clock says;
// - at or after the cutoff hour (default 15:00) the order counts from the next day;
// - with skipSunday, Sunday is neither a sending day nor a delivery day: a start on Sunday (an order on Sunday, or on
//   Saturday after the cutoff) moves to Monday, and a Sunday is never counted or shown;
// - the window is start + min .. start + max delivery days: Kosovo (XK) 1–2, Albania (AL) and North Macedonia (MK)
//   the "abroad" window (default 2–4); any other country uses Kosovo's window (as snippets/beta-delivery.liquid does),
//   but no country name and no price unless its config entry or a passed price gives one (the cart shows the line
//   only for XK / AL / MK: snippets/shipping-and-delivery-cart.liquid).
// The same rules as the product page's "Arritja" row (assets/beta-pdp.js, window.BetaPdpDelivery), duplicated here
// on purpose (that file belongs to the product page); see docs/THEME.md for the TODO to share this one.
//
// API (window.BetaEta, unit-tested in tests/beta-eta.jxa.js):
//   compute(country, now, cfg) → { country, start, from, to, minDays, maxDays }   (dates = UTC midnights)
//   format(result, lang, texts) → { title, days, single }                          (lang 'sq' | 'en')
//   fill(el, options) → the result + texts for one [data-beta-eta] element (options: country, price, now; the
//                       country and a passed price are kept on the element for later refreshes)
//   fillAll(root)
window.BetaEta = window.BetaEta || (function () {
  'use strict';

  var DAY_MS = 86400000;
  var DEFAULTS = {
    timeZone: 'Europe/Belgrade',
    cutoff: 15,
    skipSunday: true,
    countries: { XK: { min: 1, max: 2 }, AL: { min: 2, max: 4 }, MK: { min: 2, max: 4 } }
  };
  // Built-in texts; the snippet passes the theme locale's (beta.eta.*, date_time.month.*), which win.
  var TEXTS = {
    sq: {
      months: ['janar', 'shkurt', 'mars', 'prill', 'maj', 'qershor', 'korrik', 'gusht', 'shtator', 'tetor', 'nëntor', 'dhjetor'],
      // after "Arrin" the day takes the accusative ("Arrin të mërkurën"), index 0 = Sunday
      weekdays: ['të dielën', 'të hënën', 'të martën', 'të mërkurën', 'të enjten', 'të premten', 'të shtunën'],
      // a date after a verb takes "më" ("Arrin më 28 tetor"); "për" + a span ("Arrin për 1–2 ditë pune")
      day: 'Arrin [weekday], [day] [month]',
      range: 'Arrin më [from]–[to] [month]',
      rangeMonths: 'Arrin më [from] [from_month] – [to] [to_month]',
      within: 'Arrin për [days]',
      days: '[min]–[max] ditë pune',
      daysOne: '[days] ditë pune',
      daysMany: '[days] ditë pune',
      price: 'Dërgesa [price]',
      // short indefinite names, like the shipping zone "Shqipëri & Maqedoni" (one line on a 360px phone)
      names: { XK: 'Kosovë', AL: 'Shqipëri', MK: 'Maqedoni' }
    },
    en: {
      months: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
      weekdays: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
      day: 'Arrives [weekday], [month] [day]',
      range: 'Arrives [month] [from]–[to]',
      rangeMonths: 'Arrives [from_month] [from] – [to_month] [to]',
      within: 'Arrives within [days]',
      days: '[min]–[max] working days',
      daysOne: '[days] working day',
      daysMany: '[days] working days',
      price: 'Delivery [price]',
      names: { XK: 'Kosovo', AL: 'Albania', MK: 'North Macedonia' }
    }
  };

  function has(obj, key) {
    return obj !== null && typeof obj === 'object' && Object.prototype.hasOwnProperty.call(obj, key) &&
      obj[key] !== undefined && obj[key] !== null;
  }

  function toInt(value, fallback) {
    var number = parseInt(value, 10);
    return isNaN(number) ? fallback : number;
  }

  function code(country) {
    var c = String(country || '').trim().toUpperCase();
    return /^[A-Z]{2}$/.test(c) ? c : 'XK';
  }

  // { year, month (1-12), day, hour (0-23) } of `date` in `timeZone`; without time zone support the device clock.
  function localParts(date, timeZone) {
    try {
      var parts = {};
      new Intl.DateTimeFormat('en-US', {
        timeZone: timeZone || DEFAULTS.timeZone,
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        hourCycle: 'h23'
      }).formatToParts(date).forEach(function (part) {
        parts[part.type] = part.value;
      });
      var result = {
        year: parseInt(parts.year, 10),
        month: parseInt(parts.month, 10),
        day: parseInt(parts.day, 10),
        hour: parseInt(parts.hour, 10) % 24
      };
      if (result.year && result.month && result.day && result.hour >= 0) return result;
    } catch (error) {
      // no Intl time zone support: fall through to the device clock
    }
    return { year: date.getFullYear(), month: date.getMonth() + 1, day: date.getDate(), hour: date.getHours() };
  }

  // The delivery window of a country: cfg.countries[XX], else the built-in one (XK 1–2, AL / MK 2–4); any other
  // country uses Kosovo's (as snippets/beta-delivery.liquid does). min above max → max for both; negative → 0.
  function windowFor(country, cfg) {
    cfg = cfg || {};
    var c = code(country);
    var table = has(cfg, 'countries') ? cfg.countries : {};
    var builtIn = DEFAULTS.countries[c] || DEFAULTS.countries.XK;
    var src = builtIn;
    if (has(table, c)) src = table[c];
    else if (!DEFAULTS.countries[c] && has(table, 'XK')) src = table.XK;
    var max = Math.max(0, toInt(src.max, builtIn.max));
    var min = Math.min(Math.max(0, toInt(src.min, builtIn.min)), max);
    return { min: min, max: max };
  }

  // Calendar dates are UTC midnights, so adding days never meets a daylight-saving change.
  function calendarDate(year, month, day) {
    return new Date(Date.UTC(year, month - 1, day));
  }

  function isClosed(date, cfg) {
    var skip = has(cfg, 'skipSunday') ? cfg.skipSunday : DEFAULTS.skipSunday;
    return skip !== false && date.getUTCDay() === 0;
  }

  function nextOpenDay(date, cfg) {
    while (isClosed(date, cfg)) date = new Date(date.getTime() + DAY_MS);
    return date;
  }

  function addDeliveryDays(start, days, cfg) {
    var date = start;
    for (var i = 0; i < days; i++) date = nextOpenDay(new Date(date.getTime() + DAY_MS), cfg);
    return date;
  }

  // now: a Date (read in cfg.timeZone) or { year, month, day, hour } already in shop time.
  function compute(country, now, cfg) {
    cfg = cfg || {};
    var c = code(country);
    var parts = now instanceof Date || (now && typeof now.getTime === 'function')
      ? localParts(now, has(cfg, 'timeZone') ? cfg.timeZone : DEFAULTS.timeZone)
      : now;
    var win = windowFor(c, cfg);
    var cutoff = toInt(has(cfg, 'cutoff') ? cfg.cutoff : DEFAULTS.cutoff, DEFAULTS.cutoff);
    var start = calendarDate(parts.year, parts.month, parts.day);
    if (parts.hour >= cutoff) start = new Date(start.getTime() + DAY_MS);
    start = nextOpenDay(start, cfg);
    return {
      country: c,
      start: start,
      from: addDeliveryDays(start, win.min, cfg),
      to: addDeliveryDays(start, win.max, cfg),
      minDays: win.min,
      maxDays: win.max
    };
  }

  function textsFor(lang, texts) {
    var base = TEXTS[lang] || (String(lang || '').slice(0, 2) === 'sq' ? TEXTS.sq : TEXTS.en);
    var out = {};
    Object.keys(base).forEach(function (key) { out[key] = base[key]; });
    if (texts) {
      Object.keys(texts).forEach(function (key) {
        var value = texts[key];
        if (value === undefined || value === null || value === '') return;
        if ((key === 'months' && value.length !== 12) || (key === 'weekdays' && value.length !== 7)) return;
        out[key] = value;
      });
    }
    return out;
  }

  function template(str, values) {
    return String(str).replace(/\[(\w+)\]/g, function (match, key) {
      return Object.prototype.hasOwnProperty.call(values, key) ? values[key] : match;
    });
  }

  function daysText(minDays, maxDays, t) {
    if (minDays === maxDays) return template(maxDays === 1 ? t.daysOne : t.daysMany, { days: maxDays });
    return template(t.days, { min: minDays, max: maxDays });
  }

  // result (compute) → { title: "Arrin më 28–29 tetor", days: "1–2 ditë pune", single }
  function format(result, lang, texts) {
    var t = textsFor(lang, texts);
    var from = result.from;
    var to = result.to;
    var fromMonth = t.months[from.getUTCMonth()];
    var toMonth = t.months[to.getUTCMonth()];
    var title;
    var single = from.getTime() === to.getTime();
    if (single) {
      title = template(t.day, { weekday: t.weekdays[from.getUTCDay()], day: from.getUTCDate(), month: fromMonth });
    } else if (from.getUTCFullYear() === to.getUTCFullYear() && from.getUTCMonth() === to.getUTCMonth()) {
      title = template(t.range, { from: from.getUTCDate(), to: to.getUTCDate(), month: fromMonth });
    } else {
      title = template(t.rangeMonths, { from: from.getUTCDate(), from_month: fromMonth, to: to.getUTCDate(), to_month: toMonth });
    }
    return { title: title, days: daysText(result.minDays, result.maxDays, t), single: single };
  }

  // Fallback line without dates ("Arrin për 1–2 ditë pune"), as Liquid prints it.
  function within(minDays, maxDays, lang, texts) {
    var t = textsFor(lang, texts);
    return template(t.within, { days: daysText(minDays, maxDays, t) });
  }

  function isoDay(date) {
    return date.toISOString().slice(0, 10);
  }

  function config(el) {
    if (el._betaEtaConfig) return el._betaEtaConfig;
    var node = el.querySelector('script[data-beta-eta-config]');
    var cfg = {};
    if (node) {
      try {
        cfg = JSON.parse(node.textContent) || {};
      } catch (error) {
        cfg = {};
      }
    }
    el._betaEtaConfig = cfg;
    return cfg;
  }

  function setPart(el, selector, text) {
    var node = el.querySelector(selector);
    if (!node) return;
    node.textContent = text || '';
    if (text) node.removeAttribute('hidden');
    else node.setAttribute('hidden', '');
  }

  // One [data-beta-eta] element (snippets/beta-arrival.liquid). options.country switches the country (the checkout's
  // select), options.price replaces the price text ('' hides it; a quote's "5,00 €"), options.now is for tests.
  // The country and a passed price stay on the element (data-country, data-price; the snippet's own "price" parameter
  // prints data-price too), so a later fill without options (fillAll on visibilitychange / pageshow, a re-run x-init)
  // keeps them. A kept price is dropped by price: null (back to the setting's price) and by a country switch without a
  // price (it was the old country's). On any error the text printed by Liquid stays.
  function fill(el, options) {
    if (!el || !el.querySelector) return null;
    options = options || {};
    try {
      var cfg = config(el);
      var previous = el.getAttribute('data-country');
      var c = code(has(options, 'country') ? options.country : (previous || cfg.country));
      var entry = (cfg.countries && cfg.countries[c]) || {};
      var texts = cfg.texts || {};
      var lang = cfg.lang || 'sq';
      var t = textsFor(lang, texts);
      var result = compute(c, options.now || new Date(), cfg);
      var out = format(result, lang, texts);
      var name = entry.name || (t.names && t.names[c]) || '';
      var kept = el.getAttribute('data-price');
      if (has(options, 'price')) kept = String(options.price);
      else if (Object.prototype.hasOwnProperty.call(options, 'price') && options.price === null) kept = null;
      else if (previous && code(previous) !== c) kept = null;
      var price = kept !== null && kept !== undefined ? kept : (has(entry, 'price') ? String(entry.price) : '');
      price = price.trim();
      setPart(el, '[data-beta-eta-title]', out.title);
      setPart(el, '[data-beta-eta-days]', out.days);
      setPart(el, '[data-beta-eta-country]', name);
      setPart(el, '[data-beta-eta-price]', price ? template(t.price, { price: price }) : '');
      el.setAttribute('data-country', c);
      if (kept !== null && kept !== undefined) el.setAttribute('data-price', kept);
      else el.removeAttribute('data-price');
      el.setAttribute('data-beta-eta-from', isoDay(result.from));
      el.setAttribute('data-beta-eta-to', isoDay(result.to));
      el.setAttribute('data-beta-eta-state', 'dates');
      result.title = out.title;
      result.days = out.days;
      result.name = name;
      result.price = price;
      return result;
    } catch (error) {
      return null;
    }
  }

  function fillAll(root) {
    var scope = root && root.querySelectorAll ? root : (typeof document !== 'undefined' ? document : null);
    if (!scope) return 0;
    var list = scope.querySelectorAll('[data-beta-eta]');
    for (var i = 0; i < list.length; i++) fill(list[i]);
    return list.length;
  }

  return {
    compute: compute,
    format: format,
    within: within,
    windowFor: windowFor,
    localParts: localParts,
    fill: fill,
    fillAll: fillAll,
    texts: TEXTS
  };
})();

// Browser wiring: fill what is on the page now; elements re-rendered later (cart updates re-render the drawer and the
// cart page's delivery wrapper through the Section Rendering API) call BetaEta.fill from their own x-init. A tab left
// open (or restored by Back) is recomputed when it shows again.
(function () {
  if (typeof document === 'undefined' || window.BetaEta.wired) return;
  window.BetaEta.wired = true;
  var run = function () { window.BetaEta.fillAll(document); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', run);
  else run();
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'visible') run();
  });
  window.addEventListener('pageshow', function (event) {
    if (event.persisted) run();
  });
})();
